<template>
    <!-- Header bar -->
    <div class="fhr_content_layout_header">
      <h1 class="fhr_page_title">
        <i class="fcom_icon" v-html="icons.settings"></i>
        {{ __('event_settings') }}
      </h1>
      <div class="fhr_page_actions">
        <button class="el-button fcom_secondary_button" @click="$emit('cancel')">
          {{ __('cancel') }}
        </button>
        <button
          :class="['el-button fcom_primary_button', { fcom_btn_saved: saveSuccess }]"
          :disabled="saving"
          @click="saveSettings"
        >
          <template v-if="saveSuccess">{{ __('save_success') }}</template>
          <template v-else-if="saving">{{ __('saving') }}</template>
          <template v-else>{{ __('save_settings') }}</template>
        </button>
      </div>
    </div>

    <!-- Main content wrapper -->
    <div class="fhr_content_layout_body" style="max-width: 1300px; margin: 0 auto; padding: 24px 20px;">
      <!-- Error message -->
      <div v-if="globalError" class="fcom_alert fcom_alert_error">{{ globalError }}</div>

      <!-- Save toast notification -->
      <Transition name="fcom_toast">
        <div v-if="saveSuccess" class="fcom_save_toast">
          <span class="fcom_save_toast-icon">✓</span>
          {{ __('settings_saved_success') }}
        </div>
      </Transition>

      <!-- Loading state -->
      <div v-if="loadingSettings" style="text-align:center; padding: 60px">
        <div class="fcom_spinner"></div>
      </div>

      <div v-else class="fcom_settings_layout">
        <div class="fcom_settings_wrapper">
          <div class="fcom_settings_cards">

          <!-- ===== SECTION: Date & Time ===== -->
          <div class="fcom_card" id="settings-datetime">
            <div class="fcom_card-header">
              <i class="fcom_icon fcom_card-icon" v-html="icons.calendarBd"></i>
              <div>
                <div class="fcom_card-title">{{ __('date_and_time') }}</div>
                <div class="fcom_card-desc">{{ __('date_time_display_desc') }}</div>
              </div>
            </div>
            <div class="fcom_card_body">

              <!-- Date format grid (short / mid / long) -->
              <div class="fcom_settings_grid">

                <!-- Short date format -->
                <div class="fcom_settings_field">
                  <div class="fcom_field_label_row">
                    <label class="fcom_field_label">{{ __('date_format_short') }}</label>
                    <span class="fcom_date_format_hint" :title="__('date_format_short_hint')">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M12 16v-4M12 8h.01"/>
                      </svg>
                    </span>
                  </div>
                  <div class="fcom_date_format_input_row">
                    <input
                      v-model="settings.date_format_short"
                      type="text"
                      class="fcom_date_format_input"
                      placeholder="MMM d"
                    />
                    <button
                      :class="['fcom_date_format_save', { 'is-saved': formatSaved.short }]"
                      :title="formatSaved.short ? __('saved') : __('save')"
                      @click="saveFormatField('date_format_short', settings.date_format_short)"
                    >
                      <svg v-if="formatSaved.short" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </button>
                  </div>
                  <div class="fcom_date_format_preview">
                    <span class="fcom_preview_label">{{ __('preview') }}:</span>
                    <span class="fcom_preview_value">{{ previewShort }}</span>
                  </div>
                  <div class="fcom_date_format_presets">
                    <button
                      v-for="preset in shortDatePresets"
                      :key="preset.value"
                      type="button"
                      :class="['fcom_date_format_preset', { active: settings.date_format_short === preset.value }]"
                      @click="settings.date_format_short = preset.value"
                    >{{ preset.label }}</button>
                  </div>
                </div>

                <!-- Mid date format -->
                <div class="fcom_settings_field">
                  <div class="fcom_field_label_row">
                    <label class="fcom_field_label">{{ __('date_format_mid') }}</label>
                    <span class="fcom_date_format_hint" :title="__('date_format_mid_hint')">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M12 16v-4M12 8h.01"/>
                      </svg>
                    </span>
                  </div>
                  <div class="fcom_date_format_input_row">
                    <input
                      v-model="settings.date_format_mid"
                      type="text"
                      class="fcom_date_format_input"
                      placeholder="EEE, MMM d"
                    />
                    <button
                      :class="['fcom_date_format_save', { 'is-saved': formatSaved.mid }]"
                      :title="formatSaved.mid ? __('saved') : __('save')"
                      @click="saveFormatField('date_format_mid', settings.date_format_mid)"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </button>
                  </div>
                  <div class="fcom_date_format_preview">
                    <span class="fcom_preview_label">{{ __('preview') }}:</span>
                    <span class="fcom_preview_value">{{ previewMid }}</span>
                  </div>
                  <div class="fcom_date_format_presets">
                    <button
                      v-for="preset in midDatePresets"
                      :key="preset.value"
                      type="button"
                      :class="['fcom_date_format_preset', { active: settings.date_format_mid === preset.value }]"
                      @click="settings.date_format_mid = preset.value"
                    >{{ preset.label }}</button>
                  </div>
                </div>

                <!-- Long date format -->
                <div class="fcom_settings_field">
                  <div class="fcom_field_label_row">
                    <label class="fcom_field_label">{{ __('date_format_long') }}</label>
                    <span class="fcom_date_format_hint" :title="__('date_format_long_hint')">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M12 16v-4M12 8h.01"/>
                      </svg>
                    </span>
                  </div>
                  <div class="fcom_date_format_input_row">
                    <input
                      v-model="settings.date_format_long"
                      type="text"
                      class="fcom_date_format_input"
                      placeholder="EEEE, MMMM d, yyyy"
                    />
                    <button
                      :class="['fcom_date_format_save', { 'is-saved': formatSaved.long }]"
                      :title="formatSaved.long ? __('saved') : __('save')"
                      @click="saveFormatField('date_format_long', settings.date_format_long)"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </button>
                  </div>
                  <div class="fcom_date_format_preview">
                    <span class="fcom_preview_label">{{ __('preview') }}:</span>
                    <span class="fcom_preview_value">{{ previewLong }}</span>
                  </div>
                  <div class="fcom_date_format_presets">
                    <button
                      v-for="preset in longDatePresets"
                      :key="preset.value"
                      type="button"
                      :class="['fcom_date_format_preset', { active: settings.date_format_long === preset.value }]"
                      @click="settings.date_format_long = preset.value"
                    >{{ preset.label }}</button>
                  </div>
                </div>
              </div>

              <!-- Badge month format -->
              <div class="fcom_form_row">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('badge_month_format') }}</span>
                  <span class="fcom_field_hint">{{ __('badge_month_format_hint') }}</span>
                </div>
                <div class="fcom_row_control">
                  <div class="fcom_radio_group">
                    <label class="fcom_radio_label">
                      <input v-model="settings.badge_month_format" type="radio" value="MMM" />
                      &nbsp;<span>{{ badgeMonthPreview('MMM') }}</span>
                    </label>
                    <label class="fcom_radio_label">
                      <input v-model="settings.badge_month_format" type="radio" value="MMMM" />
                      &nbsp;<span>{{ badgeMonthPreview('MMMM') }}</span>
                    </label>
                    <label class="fcom_radio_label">
                      <input v-model="settings.badge_month_format" type="radio" value="M" />
                      &nbsp;<span>{{ badgeMonthPreview('M') }}</span>
                    </label>
                  </div>
                </div>
              </div>

              <!-- Time format -->
              <div class="fcom_form_row">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('time_format') }}</span>
                  <span class="fcom_field_hint">{{ __('time_format_hint') }}</span>
                </div>
                <div class="fcom_row_control">
                  <div class="fcom_radio_group">
                    <label class="fcom_radio_label">
                      <input v-model="settings.time_format" type="radio" value="12h" />
                      &nbsp;<span>{{ __('12_hour') }} ({{ timePreview.time12h }})</span>
                    </label>
                    <label class="fcom_radio_label">
                      <input v-model="settings.time_format" type="radio" value="24h" />
                      &nbsp;<span>{{ __('24_hour') }} ({{ timePreview.time24h }})</span>
                    </label>
                  </div>
                </div>
              </div>

              <!-- Default timezone -->
              <div class="fcom_form_row">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('default_timezone') }}</span>
                  <span class="fcom_field_hint">{{ __('default_timezone_hint') }}</span>
                </div>
                <div class="fcom_row_control">
                  <select v-model="settings.default_timezone" class="el-select">
                    <option v-for="(label, tzKey) in timezoneList" :key="tzKey" :value="tzKey">
                      {{ label }}
                    </option>
                  </select>
                </div>
              </div>

              <!-- Enabled timezones -->
              <div class="fcom_form_row">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('enabled_timezones') }}</span>
                  <span class="fcom_field_hint">{{ __('enabled_timezones_hint') }}</span>
                </div>
                <div class="fcom_row_control">
                  <div class="fcom_timezone_picker">
                    <div class="fcom_timezone_actions">
                      <button type="button" class="el-button el-button--text el-button--small" @click="selectAllTimezones">
                        {{ __('select_all') }}
                      </button>
                      <button type="button" class="el-button el-button--text el-button--small" @click="clearAllTimezones">
                        {{ __('clear_all') }}
                      </button>
                    </div>
                    <div class="fcom_timezone_list">
                      <label
                        v-for="(label, tzKey) in timezoneList"
                        :key="tzKey"
                        class="fcom_timezone_item"
                      >
                        <input
                          type="checkbox"
                          :value="tzKey"
                          v-model="settings.enabled_timezones"
                        />
                        <span>{{ label }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Show timezone -->
              <div class="fcom_form_row">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('show_timezone') }}</span>
                  <span class="fcom_field_hint">{{ __('show_timezone_hint') }}</span>
                </div>
                <div class="fcom_row_control">
                  <label class="fcom_toggle">
                    <input type="checkbox" v-model="settings.show_timezone" />
                    <span class="fcom_toggle-slider"></span>
                  </label>
                </div>
              </div>

              <!-- Show local timezone -->
              <div class="fcom_form_row">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('show_local_timezone') }}</span>
                  <span class="fcom_field_hint">{{ __('show_local_timezone_hint') }}</span>
                </div>
                <div class="fcom_row_control">
                  <label class="fcom_toggle">
                    <input type="checkbox" v-model="settings.show_local_timezone" />
                    <span class="fcom_toggle-slider"></span>
                  </label>
                </div>
              </div>

              <!-- Local timezone mode (shown only when show_local_timezone is on) -->
              <div v-if="settings.show_local_timezone" class="fcom_form_row fcom_form_row--indented">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('local_timezone_mode') }}</span>
                  <span class="fcom_field_hint">{{ __('local_timezone_mode_hint') }}</span>
                </div>
                <div class="fcom_row_control">
                  <select v-model="settings.local_timezone_mode" class="el-select">
                    <option value="local_only">{{ __('local_only') }}</option>
                    <option value="event_plus_local">{{ __('event_plus_local') }}</option>
                    <option value="offset_only">{{ __('offset_only') }}</option>
                    <option value="both_labeled">{{ __('both_labeled') }}</option>
                  </select>
                </div>
              </div>

              <!-- Week starts on -->
              <div class="fcom_form_row">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('week_starts_on') }}</span>
                  <span class="fcom_field_hint">{{ __('week_starts_hint') }}</span>
                </div>
                <div class="fcom_row_control">
                  <div class="fcom_radio_group">
                    <label class="fcom_radio_label">
                      <input v-model="settings.week_start_day" type="radio" value="monday" />
                      &nbsp;<span>{{ __('monday') }}</span>
                    </label>
                    <label class="fcom_radio_label">
                      <input v-model="settings.week_start_day" type="radio" value="sunday" />
                      &nbsp;<span>{{ __('sunday') }}</span>
                    </label>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <!-- ===== SECTION: Event List Display ===== -->
          <div class="fcom_card" id="settings-display">
            <div class="fcom_card-header">
              <i class="fcom_icon fcom_card-icon" v-html="icons.listViewIcon"></i>
              <div>
                <div class="fcom_card-title">{{ __('event_list_display') }}</div>
                <div class="fcom_card-desc">{{ __('event_list_display_desc') }}</div>
              </div>
            </div>
            <div class="fcom_card_body">

              <!-- Default view -->
              <div class="fcom_form_row">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('default_view') }}</span>
                  <span class="fcom_field_hint">{{ __('default_view_hint') }}</span>
                </div>
                <div class="fcom_row_control">
                  <div class="fcom_radio_group">
                    <label class="fcom_radio_label">
                      <input v-model="settings.default_view" type="radio" value="grid" />
                      &nbsp;<span>{{ __('grid') }}</span>
                    </label>
                    <label class="fcom_radio_label">
                      <input v-model="settings.default_view" type="radio" value="list" />
                      &nbsp;<span>{{ __('list') }}</span>
                    </label>
                    <label class="fcom_radio_label">
                      <input v-model="settings.default_view" type="radio" value="calendar" />
                      &nbsp;<span>{{ __('month') }}</span>
                    </label>
                    <label class="fcom_radio_label">
                      <input v-model="settings.default_view" type="radio" value="week" />
                      &nbsp;<span>{{ __('week') }}</span>
                    </label>
                  </div>
                </div>
              </div>

              <!-- Scroll mode -->
              <div class="fcom_form_row">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('scroll_mode') }}</span>
                  <span class="fcom_field_hint">{{ __('scroll_mode_hint') }}</span>
                </div>
                <div class="fcom_row_control">
                  <div class="fcom_radio_group">
                    <label class="fcom_radio_label">
                      <input v-model="settings.scroll_mode" type="radio" value="pagination" />
                      &nbsp;<span>{{ __('pagination') }}</span>
                    </label>
                    <label class="fcom_radio_label">
                      <input v-model="settings.scroll_mode" type="radio" value="infinite" />
                      &nbsp;<span>{{ __('infinite_scroll') }}</span>
                    </label>
                  </div>
                </div>
              </div>

              <!-- Items per page -->
              <div class="fcom_form_row">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('items_per_page') }}</span>
                  <span class="fcom_field_hint">{{ __('items_per_page_hint') }}</span>
                </div>
                <div class="fcom_row_control">
                  <select v-model.number="settings.items_per_page" class="el-select" style="max-width: 120px">
                    <option :value="6">6</option>
                    <option :value="9">9</option>
                    <option :value="12">12</option>
                    <option :value="18">18</option>
                    <option :value="24">24</option>
                  </select>
                </div>
              </div>

              <!-- Show summary on cards -->
              <div class="fcom_form_row">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('show_summary_on_cards_label') }}</span>
                  <span class="fcom_field_hint">{{ __('show_summary_on_cards_hint') }}</span>
                </div>
                <div class="fcom_row_control">
                  <label class="fcom_toggle">
                    <input type="checkbox" v-model="settings.show_summary_on_cards" />
                    <span class="fcom_toggle-slider"></span>
                  </label>
                </div>
              </div>

            </div>
          </div>

          <!-- ===== SECTION: Single Event Display ===== -->
          <div class="fcom_card" id="settings-single">
            <div class="fcom_card-header">
              <i class="fcom_icon fcom_card-icon" v-html="icons.eye || icons.settings"></i>
              <div>
                <div class="fcom_card-title">{{ __('single_event_display') }}</div>
                <div class="fcom_card-desc">{{ __('single_event_display_desc') }}</div>
              </div>
            </div>
            <div class="fcom_card_body">

              <!-- Show share button -->
              <div class="fcom_form_row">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('show_share_button_label') }}</span>
                  <span class="fcom_field_hint">{{ __('show_share_button_hint') }}</span>
                </div>
                <div class="fcom_row_control">
                  <label class="fcom_toggle">
                    <input type="checkbox" v-model="settings.show_share_button" />
                    <span class="fcom_toggle-slider"></span>
                  </label>
                </div>
              </div>

              <!-- Show about title -->
              <div class="fcom_form_row">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('show_about_title_label') }}</span>
                  <span class="fcom_field_hint">{{ __('show_about_title_hint') }}</span>
                </div>
                <div class="fcom_row_control">
                  <label class="fcom_toggle">
                    <input type="checkbox" v-model="settings.show_about_title" />
                    <span class="fcom_toggle-slider"></span>
                  </label>
                </div>
              </div>

            </div>
          </div>

          <!-- ===== SECTION: Access Control ===== -->
          <div class="fcom_card" id="settings-access">
            <div class="fcom_card-header">
              <i class="fcom_icon fcom_card-icon" v-html="icons.lock"></i>
              <div>
                <div class="fcom_card-title">{{ __('access_control') }}</div>
                <div class="fcom_card-desc">{{ __('access_control_desc') }}</div>
              </div>
            </div>
            <div class="fcom_card_body">

              <!-- Require login -->
              <div class="fcom_form_row">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('require_login') }}</span>
                  <span class="fcom_field_hint">{{ __('require_login_hint') }}</span>
                </div>
                <div class="fcom_row_control">
                  <label class="fcom_toggle">
                    <input type="checkbox" v-model="settings.require_login" />
                    <span class="fcom_toggle-slider"></span>
                  </label>
                </div>
              </div>

            </div>
          </div>

          <!-- ===== SECTION: Permissions Management (only if current user can_settings) ===== -->
          <div v-if="currentUserCanSettings" class="fcom_card" id="settings-permissions">
            <div class="fcom_card-header">
              <i class="fcom_icon fcom_card-icon" v-html="icons.shield"></i>
              <div>
                <div class="fcom_card-title">{{ __('permissions_management') }}</div>
                <div class="fcom_card-desc">{{ __('permissions_management_desc') }}</div>
              </div>
            </div>
            <div class="fcom_card_body">

              <div v-if="loadingPermissions" class="fcom_loading_inline">
                <div class="fcom_spinner"></div>
              </div>
              <div v-else-if="permissionsError" class="fcom_error_message">{{ permissionsError }}</div>
              <template v-else>
                <div class="fcom_permissions_intro">
                  <p>{{ __('permissions_intro') }}</p>
                </div>
                <div class="fcom_permissions_roles">
                  <div
                    v-for="role in roles"
                    :key="role.slug"
                    class="fcom_permissions_role_item"
                  >
                    <label class="fcom_permissions_role_label">
                      <input
                        type="checkbox"
                        :value="role.slug"
                        v-model="allowedRoles"
                        class="fcom_permissions_checkbox"
                      />
                      <span class="fcom_permissions_role_name">{{ role.name }}</span>
                    </label>
                    <div class="fcom_permissions_role_caps">
                      <span v-if="role.perms.can_edit_all" class="fcom_cap_badge is-success">
                        {{ __('can_edit_all') }}
                      </span>
                      <span v-else class="fcom_cap_badge">
                        {{ __('can_edit_own') }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="fcom_permissions_actions">
                  <button
                    class="el-button fcom_secondary_button"
                    :disabled="savingPermissions"
                    @click="loadPermissions"
                  >
                    {{ __('reset') }}
                  </button>
                  <button
                    class="el-button fcom_primary_button"
                    :disabled="savingPermissions"
                    @click="savePermissions"
                  >
                    {{ savingPermissions ? __('saving') : __('save_permissions') }}
                  </button>
                </div>
                <div v-if="permissionsSaveSuccess" class="fcom_success_message">
                  {{ __('permissions_saved') }}
                </div>
              </template>

            </div>
          </div>

          <!-- ===== SECTION: Category Management (only if current user can_manage) ===== -->
          <div v-if="currentUserCanManage" class="fcom_card" id="settings-categories">
            <div class="fcom_card-header">
              <i class="fcom_icon fcom_card-icon" v-html="icons.bookmark"></i>
              <div>
                <div class="fcom_card-title">{{ __('category_management') }}</div>
                <div class="fcom_card-desc">{{ __('category_management_desc') }}</div>
              </div>
            </div>
            <div class="fcom_card_body">

              <div v-if="loadingCategories" class="fcom_loading_inline">
                <div class="fcom_spinner"></div>
              </div>
              <div v-else-if="categoriesError" class="fcom_error_message">{{ categoriesError }}</div>
              <div v-else-if="categories.length === 0" class="fcom_empty_state">
                {{ __('no_categories_yet') }}
              </div>
              <div v-else class="fcom_categories_list">
                <div
                  v-for="category in categories"
                  :key="category.id"
                  class="fcom_category_management_item"
                >
                  <!-- Edit mode -->
                  <template v-if="editingCategory && editingCategory.id === category.id">
                    <input
                      v-model="editingCategory.name"
                      type="text"
                      class="fcom_form_input fcom_form_input_sm"
                      ref="editCategoryInput"
                      @keyup.enter="saveEditCategory"
                      @keyup.esc="cancelEditCategory"
                    />
                    <div class="fcom_category_edit_actions">
                      <button
                        class="el-button fcom_primary_button el-button--small"
                        :disabled="!editingCategory.name.trim() || savingCategory"
                        @click="saveEditCategory"
                      >
                        {{ savingCategory ? __('saving') : __('save') }}
                      </button>
                      <button
                        class="el-button fcom_secondary_button el-button--small"
                        :disabled="savingCategory"
                        @click="cancelEditCategory"
                      >
                        {{ __('cancel') }}
                      </button>
                    </div>
                  </template>

                  <!-- View mode -->
                  <template v-else>
                    <div class="fcom_category_info">
                      <span
                        class="fcom_category_color_dot"
                        :style="{ backgroundColor: category.color || '#3B82F6' }"
                      ></span>
                      <span class="fcom_category_name">{{ category.name }}</span>
                      <span class="fcom_category_count">
                        ({{ category.count }} {{ __('events') }})
                      </span>
                    </div>
                    <div class="fcom_category_actions">
                      <!-- Color picker -->
                      <div :class="['fcom_cat_color_picker', { 'is-open': openColorPickerId === category.id }]">
                        <button
                          class="fcom_cat_color_btn"
                          :title="__('color')"
                          @click.stop="openColorPickerId = openColorPickerId === category.id ? null : category.id"
                        >
                          <span
                            class="fcom_cat_color_preview"
                            :style="{ backgroundColor: category.color || '#3B82F6' }"
                          ></span>
                        </button>
                        <div
                          v-if="openColorPickerId === category.id"
                          class="fcom_cat_color_dropdown"
                          @click.stop
                        >
                          <button
                            v-for="swatch in colorSwatches"
                            :key="swatch.name"
                            :class="['fcom_cat_color_swatch', { active: category.color_name === swatch.name }]"
                            :style="{ backgroundColor: swatch.hex }"
                            :title="swatch.name"
                            @click="saveCategoryColor(category, swatch.name)"
                          ></button>
                          <label class="fcom_cat_color_custom" :title="__('custom_color')">
                            <input
                              type="color"
                              :value="category.color || '#3B82F6'"
                              @input="saveCategoryColor(category, $event.target.value)"
                            />
                          </label>
                        </div>
                      </div>

                      <!-- Edit button -->
                      <button
                        class="el-button el-button--text el-button--small"
                        :title="__('edit')"
                        @click="startEditCategory(category)"
                      >
                        <i class="fcom_icon" v-html="icons.edit"></i>
                      </button>

                      <!-- Delete button -->
                      <button
                        class="el-button fcom_secondary_button fca-danger el-button--small"
                        :title="__('delete')"
                        :disabled="category.count > 0"
                        @click="deleteCategory(category)"
                      >
                        <i class="fcom_icon" v-html="icons.trash"></i>
                      </button>
                    </div>
                  </template>
                </div>
              </div>

              <!-- Add new category form -->
              <div class="fcom_category_add">
                <template v-if="showAddCategory">
                  <input
                    v-model="newCategoryName"
                    type="text"
                    class="fcom_form_input fcom_form_input_sm"
                    :placeholder="__('category_name')"
                    @keyup.enter="createCategory"
                  />
                  <div class="fcom_category_add_actions">
                    <button
                      type="button"
                      class="el-button fcom_primary_button el-button--small"
                      :disabled="!newCategoryName.trim() || creatingCategory"
                      @click="createCategory"
                    >
                      {{ creatingCategory ? __('creating') : __('create') }}
                    </button>
                    <button
                      type="button"
                      class="el-button fcom_secondary_button el-button--small"
                      @click="cancelAddCategory"
                    >
                      {{ __('cancel') }}
                    </button>
                  </div>
                </template>
                <template v-else>
                  <div class="fcom_category_add_trigger">
                    <button
                      type="button"
                      class="el-button el-button--text el-button--small"
                      @click="showAddCategory = true"
                    >
                      <i class="fcom_icon" v-html="icons.plus"></i>
                      {{ __('add_new_category') }}
                    </button>
                  </div>
                </template>
              </div>

              <div v-if="categorySuccessMessage" class="fcom_success_message">
                {{ categorySuccessMessage }}
              </div>

            </div>
          </div>

          <!-- ===== SECTION: FluentCommunity Menu ===== -->
          <div class="fcom_card" id="settings-menu">
            <div class="fcom_card-header">
              <i class="fcom_icon fcom_card-icon" v-html="icons.menuIcon"></i>
              <div>
                <div class="fcom_card-title">{{ __('fluentcommunity_menu') }}</div>
                <div class="fcom_card-desc">{{ __('fluentcommunity_menu_desc') }}</div>
              </div>
            </div>
            <div class="fcom_card_body">

              <!-- Show in header -->
              <div class="fcom_form_row">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('show_in_header') }}</span>
                  <span class="fcom_field_hint">{{ __('show_in_header_hint') }}</span>
                </div>
                <div class="fcom_row_control">
                  <label class="fcom_toggle">
                    <input type="checkbox" v-model="settings.show_in_header" />
                    <span class="fcom_toggle-slider"></span>
                  </label>
                </div>
              </div>

              <!-- Header menu label & icon (shown when show_in_header) -->
              <div v-if="settings.show_in_header" class="fcom_form_row fcom_form_row--indented">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('header_menu_label') }}</span>
                  <span class="fcom_field_hint">{{ __('header_menu_label_hint') }}</span>
                </div>
                <div class="fcom_row_control fcom_icon_input_row">
                  <button
                    type="button"
                    class="fcom_icon_picker_btn"
                    v-html="settings.header_menu_icon || '📅'"
                    @click="openIconPicker('header')"
                  ></button>
                  <input
                    v-model="settings.header_menu_label"
                    type="text"
                    class="el-input"
                    :placeholder="__('events')"
                    style="max-width: 240px"
                  />
                </div>
              </div>

              <!-- Show in sidebar -->
              <div class="fcom_form_row">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('show_in_sidebar') }}</span>
                  <span class="fcom_field_hint">{{ __('show_in_sidebar_hint') }}</span>
                </div>
                <div class="fcom_row_control">
                  <label class="fcom_toggle">
                    <input type="checkbox" v-model="settings.show_in_sidebar" />
                    <span class="fcom_toggle-slider"></span>
                  </label>
                </div>
              </div>

              <!-- Sidebar position (shown when show_in_sidebar) -->
              <div v-if="settings.show_in_sidebar" class="fcom_form_row fcom_form_row--indented">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('sidebar_position') }}</span>
                  <span class="fcom_field_hint">{{ __('sidebar_position_hint') }}</span>
                </div>
                <div class="fcom_row_control">
                  <div class="fcom_radio_group">
                    <label class="fcom_radio_label">
                      <input v-model="settings.sidebar_position" type="radio" value="before" />
                      &nbsp;<span>{{ __('before_defaults') }}</span>
                    </label>
                    <label class="fcom_radio_label">
                      <input v-model="settings.sidebar_position" type="radio" value="after" />
                      &nbsp;<span>{{ __('after_defaults') }}</span>
                    </label>
                  </div>
                </div>
              </div>

              <!-- Sidebar group label (shown when show_in_sidebar) -->
              <div v-if="settings.show_in_sidebar" class="fcom_form_row fcom_form_row--indented">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('sidebar_group_label') }}</span>
                  <span class="fcom_field_hint">{{ __('sidebar_group_label_hint') }}</span>
                </div>
                <div class="fcom_row_control">
                  <input
                    v-model="settings.sidebar_group_label"
                    type="text"
                    class="el-input"
                    :placeholder="__('events')"
                    style="max-width: 240px"
                  />
                </div>
              </div>

              <!-- Sidebar item label & icon (shown when show_in_sidebar) -->
              <div v-if="settings.show_in_sidebar" class="fcom_form_row fcom_form_row--indented">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('sidebar_item_label') }}</span>
                  <span class="fcom_field_hint">{{ __('sidebar_item_label_hint') }}</span>
                </div>
                <div class="fcom_row_control fcom_icon_input_row">
                  <button
                    type="button"
                    class="fcom_icon_picker_btn"
                    v-html="settings.sidebar_menu_icon || '📅'"
                    @click="openIconPicker('sidebar')"
                  ></button>
                  <input
                    v-model="settings.sidebar_item_label"
                    type="text"
                    class="el-input"
                    :placeholder="__('events')"
                    style="max-width: 240px"
                  />
                </div>
              </div>

            </div>
          </div>

          <!-- ===== SECTION: Feed Widgets ===== -->
          <div class="fcom_card" id="settings-feed-widgets">
            <div class="fcom_card-header">
              <i class="fcom_icon fcom_card-icon" v-html="icons.listViewIcon"></i>
              <div>
                <div class="fcom_card-title">{{ __('feed_widgets') }}</div>
                <div class="fcom_card-desc">{{ __('feed_widgets_desc') }}</div>
              </div>
            </div>
            <div class="fcom_card_body">

              <!-- Feed widgets enabled -->
              <div class="fcom_form_row">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('feed_widgets_enabled') }}</span>
                  <span class="fcom_field_hint">{{ __('feed_widgets_enabled_hint') }}</span>
                </div>
                <div class="fcom_row_control">
                  <div class="fcom_radio_group">
                    <label class="fcom_radio_label">
                      <input v-model="settings.feed_widgets_enabled" type="radio" value="yes" />
                      &nbsp;<span>{{ __('yes') }}</span>
                    </label>
                    <label class="fcom_radio_label">
                      <input v-model="settings.feed_widgets_enabled" type="radio" value="no" />
                      &nbsp;<span>{{ __('no') }}</span>
                    </label>
                  </div>
                </div>
              </div>

              <!-- Feed widget title (when enabled) -->
              <div v-if="settings.feed_widgets_enabled !== 'no'" class="fcom_form_row">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('feed_widget_title') }}</span>
                  <span class="fcom_field_hint">{{ __('feed_widget_title_hint') }}</span>
                </div>
                <div class="fcom_row_control">
                  <input
                    v-model="settings.feed_widget_title"
                    type="text"
                    class="el-input"
                    :placeholder="__('upcoming_events')"
                    style="max-width: 240px"
                  />
                </div>
              </div>

              <!-- Feed widget count (when enabled) -->
              <div v-if="settings.feed_widgets_enabled !== 'no'" class="fcom_form_row">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('feed_widget_count') }}</span>
                  <span class="fcom_field_hint">{{ __('feed_widget_count_hint') }}</span>
                </div>
                <div class="fcom_row_control">
                  <input
                    v-model.number="settings.feed_widget_count"
                    type="number"
                    class="el-input"
                    min="1"
                    max="10"
                    style="max-width: 100px"
                  />
                </div>
              </div>

              <!-- Feed widget show pinned (when enabled) -->
              <div v-if="settings.feed_widgets_enabled !== 'no'" class="fcom_form_row">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('feed_widget_show_pinned') }}</span>
                  <span class="fcom_field_hint">{{ __('feed_widget_show_pinned_hint') }}</span>
                </div>
                <div class="fcom_row_control">
                  <label class="fcom_toggle">
                    <input type="checkbox" v-model="settings.feed_widget_show_pinned" />
                    <span class="fcom_toggle-slider"></span>
                  </label>
                </div>
              </div>

            </div>
          </div>

          <!-- ===== SECTION: User Permission Check ===== -->
          <div class="fcom_card" id="settings-usercheck">
            <div class="fcom_card-header">
              <i class="fcom_icon fcom_card-icon" v-html="icons.userCheck || icons.settings"></i>
              <div>
                <div class="fcom_card-title">{{ __('user_permission_check') }}</div>
                <div class="fcom_card-desc">{{ __('user_permission_check_desc') }}</div>
              </div>
            </div>
            <div class="fcom_card_body">

              <div class="fcom_form_row">
                <div class="fcom_row_label">
                  <span class="fcom_field_label">{{ __('username_or_email') }}</span>
                </div>
                <div class="fcom_row_control fcom_check_input_row">
                  <input
                    v-model="checkUsername"
                    type="text"
                    class="el-input"
                    :placeholder="__('username_or_email_placeholder')"
                    style="max-width: 280px"
                    @keyup.enter="checkUserPermissions"
                  />
                  <button
                    class="el-button fcom_secondary_button"
                    :disabled="checkingUser || !checkUsername"
                    @click="checkUserPermissions"
                  >
                    {{ checkingUser ? __('checking') : __('check') }}
                  </button>
                </div>
              </div>

              <div v-if="checkUserError" class="fcom_error_message">{{ checkUserError }}</div>

              <div v-if="checkedUserResult" class="fcom_check_result">
                <!-- WP user info -->
                <div class="fcom_check_user_info">
                  <div class="fcom_check_display_name">{{ checkedUserResult.user.display_name }}</div>
                  <div class="fcom_check_meta">
                    @{{ checkedUserResult.user.username }} &middot; {{ checkedUserResult.user.email }}
                  </div>
                  <div class="fcom_check_roles">
                    {{ __('wp_roles') }}: {{ checkedUserResult.user.wp_roles.join(', ') }}
                  </div>
                </div>

                <!-- FC info -->
                <div v-if="checkedUserResult.fc" class="fcom_check_fc_info">
                  <div class="fcom_check_section_title">{{ __('fluent_community') }}</div>
                  <div class="fcom_check_badges">
                    <span
                      v-if="checkedUserResult.fc.status"
                      :class="['fcom_check_badge', checkedUserResult.fc.status === 'active' ? 'is-success' : 'is-warning']"
                    >
                      {{ checkedUserResult.fc.status }}
                    </span>
                    <span
                      v-for="role in (checkedUserResult.fc.manager_roles || [])"
                      :key="role"
                      class="fcom_check_badge is-primary"
                    >{{ role }}</span>
                    <span
                      v-for="role in (checkedUserResult.fc.community_roles || [])"
                      :key="'c-' + role"
                      class="fcom_check_badge is-primary"
                    >{{ role }}</span>
                    <span v-if="checkedUserResult.fc.is_verified" class="fcom_check_badge is-success">
                      {{ __('verified') }}
                    </span>
                    <span
                      v-if="!checkedUserResult.fc.manager_roles?.length && !checkedUserResult.fc.community_roles?.length"
                      class="fcom_check_badge"
                    >
                      {{ __('member_no_manager_role') }}
                    </span>
                  </div>
                  <div v-if="checkedUserResult.fc.spaces?.length" class="fcom_check_spaces">
                    {{ __('spaces') }}: {{ checkedUserResult.fc.spaces.map(s => `#${s.space_id} (${s.role})`).join(', ') }}
                  </div>
                </div>

                <!-- Events permissions -->
                <div class="fcom_check_events_info">
                  <div class="fcom_check_section_title">{{ __('events_permissions') }}</div>
                  <div class="fcom_check_badges">
                    <span :class="['fcom_check_badge', checkedUserResult.events.can_manage_events ? 'is-success' : 'is-danger']">
                      {{ checkedUserResult.events.can_manage_events ? '✓' : '✗' }} {{ __('manage_events_perm') }}
                    </span>
                    <span :class="['fcom_check_badge', checkedUserResult.events.can_create ? 'is-success' : 'is-danger']">
                      {{ checkedUserResult.events.can_create ? '✓' : '✗' }} {{ __('create_events_perm') }}
                    </span>
                    <span :class="['fcom_check_badge', checkedUserResult.events.can_edit_all ? 'is-success' : 'is-danger']">
                      {{ checkedUserResult.events.can_edit_all ? '✓' : '✗' }} {{ __('edit_all_events_perm') }}
                    </span>
                    <span :class="['fcom_check_badge', checkedUserResult.events.can_access_settings ? 'is-success' : 'is-danger']">
                      {{ checkedUserResult.events.can_access_settings ? '✓' : '✗' }} {{ __('settings_access_perm') }}
                    </span>
                  </div>
                  <div class="fcom_check_roles_detail">
                    {{ __('fc_roles_detected') }}: {{ checkedUserResult.events.fc_roles_detected.join(', ') || 'none' }}<br />
                    {{ __('allowed_roles') }}: {{ checkedUserResult.events.allowed_roles.join(', ') }}
                  </div>
                </div>

                <!-- FC global permissions -->
                <div
                  v-if="Object.keys(checkedUserResult.fc?.permissions || {}).length"
                  class="fcom_check_fc_perms"
                >
                  <div class="fcom_check_section_title">{{ __('fc_global_permissions') }}</div>
                  <div class="fcom_check_badges">
                    <span
                      v-for="(value, key) in checkedUserResult.fc.permissions"
                      :key="key"
                      :class="['fcom_check_badge', value ? 'is-success' : 'is-danger']"
                    >
                      {{ value ? '✓' : '✗' }} {{ key }}
                    </span>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <!-- ===== SECTION: Custom Translations ===== -->
          <div class="fcom_card" id="settings-translations">
            <div class="fcom_card-header">
              <i class="fcom_icon fcom_card-icon" v-html="icons.translate"></i>
              <div>
                <div class="fcom_card-title">{{ __('custom_translations') }}</div>
                <div class="fcom_card-desc">{{ __('custom_translations_desc') }}</div>
              </div>
            </div>
            <div class="fcom_card_body--padded" style="display:flex;align-items:center;gap:16px;">
              <button class="el-button fcom_secondary_button" @click="showTranslationsModal = true">
                <i class="fcom_icon" v-html="icons.edit"></i>
                {{ __('edit_translations') }}
              </button>
              <span class="fcom_form_hint">
                {{ customTranslationsCount }} {{ __('translations_customized') }}
              </span>
            </div>
          </div>

          </div><!-- /fcom_settings_cards -->

          <!-- Sticky side nav -->
          <nav class="fcom_settings_nav">
            <div class="fcom_settings_nav-title">{{ __('settings') }}</div>
            <a
              v-for="section in navSections"
              :key="section.id"
              :class="['fcom_settings_nav-item', { 'is-active': activeSection === section.id }]"
              href="#"
              @click.prevent="scrollToSection(section.id)"
            >
              <i class="fcom_icon" v-html="section.icon"></i>
              <span>{{ section.label }}</span>
            </a>
          </nav>
        </div><!-- /fcom_settings_wrapper -->
      </div>
    </div>

    <!-- Icon Picker Modal -->
    <IconPickerModal
      :show="iconPickerVisible"
      :title="iconPickerContext === 'header'
        ? __('icon_picker_title_header', { label: __('header_menu_label') })
        : __('icon_picker_title_sidebar', { label: __('sidebar_item_label') })"
      :current-icon="iconPickerContext === 'header' ? settings.header_menu_icon : settings.sidebar_menu_icon"
      @select="onIconSelected"
      @close="iconPickerVisible = false"
    />

    <!-- Translations Modal -->
    <div
      v-if="showTranslationsModal"
      class="fcom_modal_overlay"
      @click.self="showTranslationsModal = false"
    >
      <div class="fcom_modal" style="max-width:680px;">
        <div class="fcom_modal_header">
          <h3>{{ __('custom_translations') }}</h3>
          <button class="fcom_modal_close" @click="showTranslationsModal = false">&times;</button>
        </div>
        <div class="fcom_modal_body">
          <p class="fcom_translations_help">{{ __('custom_translations_help') }}</p>
          <div class="fcom_translations_search">
            <input
              v-model="translationSearch"
              type="text"
              :placeholder="__('search_translations_placeholder')"
            />
          </div>
          <div class="fcom_translations_count_label">
            {{ filteredTranslationsCount }} {{ __('translations_found') }}
          </div>
          <div class="fcom_translations_list">
            <div
              v-for="(defaultValue, key) in filteredTranslations"
              :key="key"
              class="fcom_translation_item"
            >
              <div class="fcom_translation_key">{{ key }}</div>
              <div class="fcom_translation_default">
                <label>{{ __('default') }}:</label>
                <span>{{ defaultValue }}</span>
              </div>
              <div class="fcom_translation_custom">
                <input
                  v-model="customTranslations[key]"
                  type="text"
                  :placeholder="__('leave_empty_for_default')"
                  @input="(e) => onSetCustomTranslation(key, e.target.value)"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="fcom_modal_footer">
          <button class="el-button fcom_secondary_button" @click="resetAllTranslations">
            {{ __('reset_all') }}
          </button>
          <button class="el-button fcom_primary_button" @click="showTranslationsModal = false">
            {{ __('done') }}
          </button>
        </div>
      </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { DateTime } from 'luxon'
import { __, getAllTranslationKeys } from '@/utils/i18n.js'
import { settingsApi, translationsApi } from '@/utils/api.js'
import icons from '@/utils/icons.js'
import IconPickerModal from '@/components/IconPickerModal.vue'

// ---------------------------------------------------------------------------
// Emits
// ---------------------------------------------------------------------------
const emit = defineEmits(['cancel'])

// ---------------------------------------------------------------------------
// Register additional icons used in this view
// ---------------------------------------------------------------------------
icons.listViewIcon = '<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none"><path opacity="0.5" d="M3 7a1 1 0 0 1 1-1h16a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1zm0 5a1 1 0 0 1 1-1h16a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1zm0 5a1 1 0 0 1 1-1h16a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1z" fill="currentColor"/><circle cx="4" cy="7" r="1" fill="currentColor"/><circle cx="4" cy="12" r="1" fill="currentColor"/><circle cx="4" cy="17" r="1" fill="currentColor"/></svg>'
icons.menuIcon = '<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none"><path opacity="0.5" fill-rule="evenodd" clip-rule="evenodd" d="M3 5a1 1 0 0 1 1-1h16a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1zm0 7a1 1 0 0 1 1-1h16a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1zm1 6a1 1 0 1 0 0 2h16a1 1 0 1 0 0-2H4z" fill="currentColor"/></svg>'
icons.shield = '<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" stroke-width="2" fill="none"/><path d="M9 12l2 2 4-4" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>'
icons.bookmark = '<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none"><path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2v16z" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>'
icons.edit = '<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
icons.trash = '<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
icons.plus = '<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
icons.userCheck = '<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="8.5" cy="7" r="4" stroke="currentColor" stroke-width="2"/><path d="M17 11l2 2 4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
icons.lock = '<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" stroke-width="2"/><path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
icons.eye = '<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/></svg>'
icons.translate = '<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none"><path d="M5 8l6 6M4 14l6-6 2-3M2 5h12M7 2h1M22 22l-5-10-5 10M16 17h6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'

// ---------------------------------------------------------------------------
// Config / globals
// ---------------------------------------------------------------------------
const config = window.FcaEventsConfig || {}

// ---------------------------------------------------------------------------
// Global state
// ---------------------------------------------------------------------------
const loadingSettings = ref(true)
const saving = ref(false)
const saveSuccess = ref(false)
const globalError = ref(null)

// ---------------------------------------------------------------------------
// Settings object (mirrors the API response)
// ---------------------------------------------------------------------------
const settings = ref({
  date_format: 'D MMMM YYYY',
  date_format_short: 'MMM d',
  date_format_mid: 'EEE, MMM d',
  date_format_long: 'EEEE, MMMM d, yyyy',
  badge_month_format: 'MMM',
  time_format: '12h',
  default_timezone: 'Europe/London',
  show_timezone: true,
  show_local_timezone: false,
  week_start_day: 'monday',
  local_timezone_mode: 'event_plus_local',
  default_view: 'grid',
  scroll_mode: 'pagination',
  items_per_page: 12,
  show_share_button: true,
  show_about_title: true,
  show_summary_on_cards: true,
  require_login: true,
  show_in_header: true,
  show_in_sidebar: true,
  sidebar_position: 'after',
  sidebar_group_label: __('events'),
  sidebar_item_label: __('events'),
  sidebar_menu_icon: '📅',
  header_menu_label: __('events'),
  header_menu_icon: '📅',
  enabled_timezones: [],
  feed_widgets_enabled: 'yes',
  feed_widget_count: 5,
  feed_widget_title: __('upcoming_events'),
  feed_widget_show_pinned: true,
})

// Timezone list loaded from API
const timezoneList = ref({})

// Individual date-format save indicators
const formatSaved = ref({ short: false, mid: false, long: false })

// ---------------------------------------------------------------------------
// Date/time preview helpers
// ---------------------------------------------------------------------------
const sampleDateTime = computed(() => {
  const locale = (document.documentElement.lang || navigator.language || 'en').replace(/_/g, '-')
  return DateTime.local().plus({ weeks: 2 }).setLocale(locale)
})

const shortDatePresets = computed(() => [
  { value: 'MMM d',      label: sampleDateTime.value.toFormat('MMM d') },
  { value: 'M/d/yy',    label: sampleDateTime.value.toFormat('M/d/yy') },
  { value: 'd/M/yy',    label: sampleDateTime.value.toFormat('d/M/yy') },
  { value: 'yyyy-MM-dd', label: sampleDateTime.value.toFormat('yyyy-MM-dd') },
  { value: 'dd/MM/yyyy', label: sampleDateTime.value.toFormat('dd/MM/yyyy') },
  { value: 'MM/dd/yyyy', label: sampleDateTime.value.toFormat('MM/dd/yyyy') },
])

const midDatePresets = computed(() => [
  { value: 'EEE, MMM d',   label: sampleDateTime.value.toFormat('EEE, MMM d') },
  { value: 'EEE, M/d',     label: sampleDateTime.value.toFormat('EEE, M/d') },
  { value: 'EEE, d MMM',   label: sampleDateTime.value.toFormat('EEE, d MMM') },
  { value: 'MMM d, yyyy',  label: sampleDateTime.value.toFormat('MMM d, yyyy') },
])

const longDatePresets = computed(() => [
  { value: 'EEEE, MMMM d, yyyy', label: sampleDateTime.value.toFormat('EEEE, MMMM d, yyyy') },
  { value: 'EEEE, d MMMM yyyy',  label: sampleDateTime.value.toFormat('EEEE, d MMMM yyyy') },
  { value: 'EEEE, MMM d, yyyy',  label: sampleDateTime.value.toFormat('EEEE, MMM d, yyyy') },
  { value: 'MMMM d, yyyy',       label: sampleDateTime.value.toFormat('MMMM d, yyyy') },
  { value: 'd MMMM yyyy',        label: sampleDateTime.value.toFormat('d MMMM yyyy') },
])

const previewShort = computed(() => {
  try {
    return sampleDateTime.value.toFormat(settings.value.date_format_short || 'MMM d')
  } catch {
    return __('invalid_format')
  }
})

const previewMid = computed(() => {
  try {
    return sampleDateTime.value.toFormat(settings.value.date_format_mid || 'EEE, MMM d')
  } catch {
    return __('invalid_format')
  }
})

const previewLong = computed(() => {
  try {
    return sampleDateTime.value.toFormat(settings.value.date_format_long || 'EEEE, MMMM d, yyyy')
  } catch {
    return __('invalid_format')
  }
})

const timePreview = computed(() => ({
  time12h: sampleDateTime.value.set({ hour: 14, minute: 30 }).toFormat('h:mm a'),
  time24h: sampleDateTime.value.set({ hour: 14, minute: 30 }).toFormat('HH:mm'),
}))

function badgeMonthPreview(fmt) {
  const locale = ((window.FcaEventsConfig || {}).locale || 'en').replace(/_/g, '-')
  return DateTime.local().setLocale(locale).toFormat(fmt).toUpperCase()
}

// ---------------------------------------------------------------------------
// API: load settings
// ---------------------------------------------------------------------------
async function loadSettings() {
  loadingSettings.value = true
  globalError.value = null
  try {
    const response = await fetch(`${config.apiBase}settings`, {
      headers: { 'X-WP-Nonce': config.nonce },
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const data = await response.json()

    if (data.timezone_list) {
      timezoneList.value = data.timezone_list
      delete data.timezone_list
    }

    // Replace settings reactively — use spread to ensure arrays trigger updates
    for (const [key, val] of Object.entries(data)) {
      settings.value[key] = val
    }

    if (!settings.value.enabled_timezones || settings.value.enabled_timezones.length === 0) {
      settings.value.enabled_timezones = Object.keys(timezoneList.value)
    }
  } catch (err) {
    globalError.value = err.message
  } finally {
    loadingSettings.value = false
  }
}

// ---------------------------------------------------------------------------
// API: save all settings
// ---------------------------------------------------------------------------
async function saveSettings() {
  saving.value = true
  saveSuccess.value = false
  try {
    const response = await fetch(`${config.apiBase}settings`, {
      method: 'POST',
      headers: {
        'X-WP-Nonce': config.nonce,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(settings.value),
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const saved = await response.json()
    window.FcaEventsConfig = window.FcaEventsConfig || {}
    window.FcaEventsConfig.settings = { ...(window.FcaEventsConfig.settings || {}), ...saved }
    window.dispatchEvent(new CustomEvent('fca-events-settings-saved'))
    saveSuccess.value = true
    setTimeout(() => { saveSuccess.value = false }, 4000)
  } catch (err) {
    globalError.value = err.message
  } finally {
    saving.value = false
  }
}

// ---------------------------------------------------------------------------
// API: save individual date format field
// ---------------------------------------------------------------------------
async function saveFormatField(field, value) {
  const key = field.replace('date_format_', '')
  try {
    const response = await fetch(`${config.apiBase}settings`, {
      method: 'POST',
      headers: {
        'X-WP-Nonce': config.nonce,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ [field]: value }),
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    formatSaved.value[key] = true
    setTimeout(() => { formatSaved.value[key] = false }, 2000)
  } catch (err) {
    globalError.value = err.message
  }
}

// ---------------------------------------------------------------------------
// Timezone helpers
// ---------------------------------------------------------------------------
function selectAllTimezones() {
  settings.value.enabled_timezones = Object.keys(timezoneList.value)
}

function clearAllTimezones() {
  settings.value.enabled_timezones = []
}

// ---------------------------------------------------------------------------
// Permissions
// ---------------------------------------------------------------------------
const currentUserCanSettings = ref(false)
const currentUserCanManage = ref(false)
const loadingPermissions = ref(false)
const savingPermissions = ref(false)
const permissionsError = ref(null)
const permissionsSaveSuccess = ref(false)
const roles = ref([])
const allowedRoles = ref([])

async function loadPermissions() {
  loadingPermissions.value = true
  permissionsError.value = null
  try {
    const response = await fetch(`${config.apiBase}permissions`, {
      headers: { 'X-WP-Nonce': config.nonce },
    })
    if (!response.ok) {
      if (response.status === 404 || response.status === 500) {
        currentUserCanSettings.value = false
        return
      }
      throw new Error(`HTTP ${response.status}`)
    }
    const data = await response.json()
    roles.value = data.roles || []
    allowedRoles.value = roles.value.filter(r => r.allowed).map(r => r.slug)
    currentUserCanSettings.value = data.current_user?.can_settings || false
    currentUserCanManage.value = data.current_user?.can_manage || false
  } catch (err) {
    permissionsError.value = err.message
    currentUserCanSettings.value = false
  } finally {
    loadingPermissions.value = false
  }
}

async function savePermissions() {
  savingPermissions.value = true
  permissionsSaveSuccess.value = false
  permissionsError.value = null
  try {
    const response = await fetch(`${config.apiBase}permissions`, {
      method: 'POST',
      headers: {
        'X-WP-Nonce': config.nonce,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ allowed_roles: allowedRoles.value }),
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    permissionsSaveSuccess.value = true
    setTimeout(() => { permissionsSaveSuccess.value = false }, 3000)
  } catch (err) {
    permissionsError.value = err.message
  } finally {
    savingPermissions.value = false
  }
}

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------
const loadingCategories = ref(false)
const categoriesError = ref(null)
const categories = ref([])
const showAddCategory = ref(false)
const newCategoryName = ref('')
const creatingCategory = ref(false)
const editingCategory = ref(null)
const savingCategory = ref(false)
const categorySuccessMessage = ref('')
const openColorPickerId = ref(null)

const colorSwatches = [
  { name: 'blue',   hex: '#3B82F6' },
  { name: 'green',  hex: '#22C55E' },
  { name: 'orange', hex: '#F97316' },
  { name: 'red',    hex: '#EF4444' },
  { name: 'purple', hex: '#A855F7' },
  { name: 'cyan',   hex: '#06B6D4' },
  { name: 'pink',   hex: '#EC4899' },
  { name: 'yellow', hex: '#EAB308' },
]

async function loadCategories() {
  loadingCategories.value = true
  categoriesError.value = null
  try {
    const response = await fetch(`${config.apiBase}categories`, {
      headers: { 'X-WP-Nonce': config.nonce },
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    categories.value = await response.json()
  } catch (err) {
    categoriesError.value = err.message
  } finally {
    loadingCategories.value = false
  }
}

async function createCategory() {
  if (!newCategoryName.value.trim()) return
  creatingCategory.value = true
  categoriesError.value = null
  categorySuccessMessage.value = ''
  try {
    const response = await fetch(`${config.apiBase}categories`, {
      method: 'POST',
      headers: {
        'X-WP-Nonce': config.nonce,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name: newCategoryName.value.trim() }),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw new Error(err.message || `HTTP ${response.status}`)
    }
    const created = await response.json()
    categories.value.push(created)
    newCategoryName.value = ''
    showAddCategory.value = false
    categorySuccessMessage.value = __('category_created_success')
    setTimeout(() => { categorySuccessMessage.value = '' }, 3000)
  } catch (err) {
    categoriesError.value = err.message
  } finally {
    creatingCategory.value = false
  }
}

function cancelAddCategory() {
  showAddCategory.value = false
  newCategoryName.value = ''
}

function startEditCategory(category) {
  editingCategory.value = { ...category }
}

async function saveEditCategory() {
  if (!editingCategory.value || !editingCategory.value.name.trim()) return
  savingCategory.value = true
  categoriesError.value = null
  categorySuccessMessage.value = ''
  try {
    const response = await fetch(`${config.apiBase}categories/${editingCategory.value.id}`, {
      method: 'PUT',
      headers: {
        'X-WP-Nonce': config.nonce,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name: editingCategory.value.name.trim() }),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw new Error(err.message || `HTTP ${response.status}`)
    }
    const updated = await response.json()
    const index = categories.value.findIndex(c => c.id === updated.id)
    if (index !== -1) categories.value[index] = updated
    editingCategory.value = null
    categorySuccessMessage.value = __('category_updated_success')
    setTimeout(() => { categorySuccessMessage.value = '' }, 3000)
  } catch (err) {
    categoriesError.value = err.message
  } finally {
    savingCategory.value = false
  }
}

function cancelEditCategory() {
  editingCategory.value = null
}

async function saveCategoryColor(category, colorValue) {
  openColorPickerId.value = null
  categoriesError.value = null
  // If it starts with #, it's a custom hex color; otherwise it's a preset name
  const payload = { name: category.name }
  if (colorValue.startsWith('#')) {
    payload.color_hex = colorValue
  } else {
    payload.color_name = colorValue
  }
  try {
    const response = await fetch(`${config.apiBase}categories/${category.id}`, {
      method: 'PUT',
      headers: {
        'X-WP-Nonce': config.nonce,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw new Error(err.message || `HTTP ${response.status}`)
    }
    const updated = await response.json()
    const index = categories.value.findIndex(c => c.id === category.id)
    if (index !== -1) categories.value[index] = updated
  } catch (err) {
    categoriesError.value = err.message
    setTimeout(() => { categoriesError.value = null }, 3000)
  }
}

async function deleteCategory(category) {
  if (category.count > 0) {
    categoriesError.value = __('cannot_delete_category_with_events')
    setTimeout(() => { categoriesError.value = null }, 3000)
    return
  }
  if (!confirm(__('confirm_delete_category', { name: category.name }))) return
  categoriesError.value = null
  categorySuccessMessage.value = ''
  try {
    const response = await fetch(`${config.apiBase}categories/${category.id}`, {
      method: 'DELETE',
      headers: { 'X-WP-Nonce': config.nonce },
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw new Error(err.message || `HTTP ${response.status}`)
    }
    categories.value = categories.value.filter(c => c.id !== category.id)
    categorySuccessMessage.value = __('category_deleted_success')
    setTimeout(() => { categorySuccessMessage.value = '' }, 3000)
  } catch (err) {
    categoriesError.value = err.message
  }
}

// Close color picker when clicking outside
function closeColorPicker() {
  openColorPickerId.value = null
}

// ---------------------------------------------------------------------------
// Icon picker
// ---------------------------------------------------------------------------
const iconPickerVisible = ref(false)
const iconPickerContext = ref('header')

function openIconPicker(context) {
  iconPickerContext.value = context
  iconPickerVisible.value = true
}

function onIconSelected(icon) {
  if (iconPickerContext.value === 'header') {
    settings.value.header_menu_icon = icon
  } else {
    settings.value.sidebar_menu_icon = icon
  }
}

// ---------------------------------------------------------------------------
// User permission check
// ---------------------------------------------------------------------------
const checkUsername = ref('')
const checkingUser = ref(false)
const checkedUserResult = ref(null)
const checkUserError = ref(null)

async function checkUserPermissions() {
  if (!checkUsername.value) return
  checkingUser.value = true
  checkedUserResult.value = null
  checkUserError.value = null
  try {
    const response = await fetch(
      `${config.apiBase}check-user?username=${encodeURIComponent(checkUsername.value)}`,
      { headers: { 'X-WP-Nonce': config.nonce } }
    )
    if (!response.ok) {
      const data = await response.json()
      throw new Error(data.message || `HTTP ${response.status}`)
    }
    checkedUserResult.value = await response.json()
  } catch (err) {
    checkUserError.value = err.message
  } finally {
    checkingUser.value = false
  }
}

// ---------------------------------------------------------------------------
// Translations
// ---------------------------------------------------------------------------
const showTranslationsModal = ref(false)
const translationSearch = ref('')

// Default translations (from PHP, before overrides)
const defaultTranslations = ref({})

// Custom overrides (from DB via API)
const customTranslations = ref({})

const customTranslationsCount = computed(() =>
  Object.values(customTranslations.value).filter(v => v !== '').length
)

const filteredTranslations = computed(() => {
  const query = translationSearch.value.toLowerCase().trim()
  const source = defaultTranslations.value
  if (!query) return source
  const result = {}
  Object.entries(source).forEach(([key, value]) => {
    if (key.toLowerCase().includes(query) || String(value).toLowerCase().includes(query)) {
      result[key] = value
    }
  })
  return result
})

const filteredTranslationsCount = computed(() => Object.keys(filteredTranslations.value).length)

async function loadCustomTranslations() {
  try {
    const data = await translationsApi.get()
    customTranslations.value = data || {}
  } catch { /* ignore */ }
}

let translationSaveTimer = null
function onSetCustomTranslation(key, value) {
  customTranslations.value = { ...customTranslations.value, [key]: value }
  // Debounce save to avoid hammering the API on every keystroke
  clearTimeout(translationSaveTimer)
  translationSaveTimer = setTimeout(saveCustomTranslations, 800)
}

async function saveCustomTranslations() {
  try {
    await translationsApi.save(customTranslations.value)
  } catch { /* ignore */ }
}

async function resetAllTranslations() {
  if (!confirm(__('confirm_reset_translations'))) return
  customTranslations.value = {}
  await translationsApi.save({})
}

// ---------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------
const activeSection = ref('settings-datetime')

const navSections = [
  { id: 'settings-datetime',    label: __('date_and_time'),          icon: icons.calendarBd },
  { id: 'settings-display',     label: __('event_list_display'),     icon: icons.listViewIcon },
  { id: 'settings-single',      label: __('single_event_display'),   icon: icons.eye },
  { id: 'settings-access',      label: __('access_control'),         icon: icons.lock },
  { id: 'settings-permissions', label: __('permissions_management'), icon: icons.shield },
  { id: 'settings-categories',  label: __('category_management'),    icon: icons.bookmark },
  { id: 'settings-menu',        label: __('fluentcommunity_menu'),   icon: icons.menuIcon },
  { id: 'settings-feed-widgets',label: __('feed_widgets'),           icon: icons.listViewIcon },
  { id: 'settings-usercheck',   label: __('user_permission_check'),  icon: icons.userCheck },
  { id: 'settings-translations',label: __('custom_translations'),    icon: icons.translate },
]

function scrollToSection(id) {
  activeSection.value = id
  const el = document.getElementById(id)
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------
onMounted(() => {
  // Guard: if user doesn't have settings permission, bail out
  const userPermissions = (window.FcaEventsConfig || {}).userPermissions
  if (!userPermissions?.can_settings) {
    emit('cancel')
    return
  }

  // Update document title
  const siteTitle = window.fluentComAdmin?.site_title || document.title
  document.title = `${__('event_settings')} - ${siteTitle}`

  // Store default translations (before any overrides, for the editor)
  defaultTranslations.value = { ...((window.FcaEventsConfig || {}).i18n || {}) }

  loadSettings()
  loadPermissions()
  loadCategories()
  loadCustomTranslations()

  document.addEventListener('click', closeColorPicker)
})

onUnmounted(() => {
  document.removeEventListener('click', closeColorPicker)
})
</script>
